import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CustomField } from '@/lib/contacts/types'
import type { CsvColumn, ImportKind, MappingOption, UploadState } from './types'

const STANDARD_MAPPING_OPTIONS: MappingOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'notes', label: 'Notes' },
  { value: 'ignore', label: "Don't import" },
]

const STANDARD_HEADER_MAPPINGS: Record<string, string> = {
  name: 'name',
  'full name': 'name',
  email: 'email',
  'email address': 'email',
  'e-mail address': 'email',
  phone: 'phone',
  'mobile tel': 'phone',
  notes: 'notes',
}

export function importMappingOptions(customFields: CustomField[]) {
  const customFieldOptions = customFields
    .filter((field) => field.active && field.field_type !== 'file')
    .map((field) => ({ value: field.label, label: `${field.label} (custom field)` }))

  return [...STANDARD_MAPPING_OPTIONS, ...customFieldOptions]
}

export function initialMappings(columns: CsvColumn[], customFields: CustomField[]) {
  const customFieldByLabel = new Map(
    customFields.map((field) => [normalizedLabel(field.label), field.label]),
  )

  return columns.reduce<Record<string, string>>((mappings, column) => {
    const normalized = normalizedLabel(column.header)
    mappings[column.header] =
      STANDARD_HEADER_MAPPINGS[normalized] ?? customFieldByLabel.get(normalized) ?? 'ignore'
    return mappings
  }, {})
}

export function importFormData({ upload, kind }: { upload: UploadState | null; kind: ImportKind }) {
  if (!upload) throw new Error('Choose a CSV before importing.')

  const formData = new FormData()
  formData.append('file', upload.file)
  formData.append('kind', kind)
  Object.entries(upload.mappings).forEach(([header, field]) => {
    formData.append(`mappings[${header}]`, field)
  })

  return formData
}

export function parseCsvColumns(content: string) {
  const [headerLine, sampleLine = ''] = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  if (!headerLine) return []

  const samples = parseCsvLine(sampleLine)
  return parseCsvLine(headerLine)
    .filter((header) => header.length > 0)
    .map((header, index) => ({ header, sample: samples[index] ?? '' }))
}

export function readFileText(file: File) {
  if ('text' in file && typeof file.text === 'function') return file.text()

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(String(reader.result ?? '')))
    reader.addEventListener('error', () => reject(reader.error ?? new Error('Could not read CSV.')))
    reader.readAsText(file)
  })
}

export function mutationMessage(error: Error | null) {
  if (!error) return null

  return apiFieldMessages(error, 'file')[0] ?? error.message
}

function parseCsvLine(line: string) {
  const values: string[] = []
  let value = ''
  let quoted = false

  for (const character of line) {
    if (character === '"') {
      quoted = !quoted
    } else if (character === ',' && !quoted) {
      values.push(value.trim())
      value = ''
    } else {
      value += character
    }
  }
  values.push(value.trim())

  return values
}

function normalizedLabel(value: string) {
  return value.trim().toLowerCase()
}
